import * as tf from '@tensorflow/tfjs';
import * as bodySegmentation from '@tensorflow-models/body-segmentation';

class AIBackgroundRemover {
  constructor() {
    this.cloudinaryCloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dtz0urit6';
    this.uploadPreset = 'cloudinary-tools';
    this.apiKey = process.env.REACT_APP_CLOUDINARY_API_KEY;
    this.isModelLoaded = true; // Always ready since it's API-based
  }

  // Initialize method (for compatibility with existing code)
  async loadModel() {
    // API-based, no model loading required
    console.log('Cloudinary AI background removal service ready');
  }

  // Generate signature using our backend endpoint (avoids CORS issues)
  async generateSignature() {
    try {
      const response = await fetch('http://localhost:5000/api/ai/test-signature', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Signature generation failed');
      }
      

      
      return {
        timestamp: data.timestamp,
        signature: data.signature
      };
      
    } catch (error) {
      console.error('Error generating signature:', error);
      throw new Error(`Failed to generate signature: ${error.message}`);
    }
  }

  // Convert file/blob to FormData for upload
  async createUploadFormData(file) {
    const formData = new FormData();
    formData.append('upload_preset', this.uploadPreset);
    formData.append('source', 'ml');
    formData.append('api_key', this.apiKey);
    
    // Generate signature
    const { timestamp, signature } = await this.generateSignature();
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    
    // Add the file
    if (file instanceof File) {
      formData.append('file', file);
    } else if (file instanceof Blob) {
      formData.append('file', file, 'image.png');
    } else {
      throw new Error('Invalid file type. Expected File or Blob.');
      }
    
    return formData;
  }

  // Convert image element to blob
  async imageElementToBlob(imageElement) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = imageElement.naturalWidth || imageElement.width;
      canvas.height = imageElement.naturalHeight || imageElement.height;
      
      ctx.drawImage(imageElement, 0, 0);
      canvas.toBlob(resolve, 'image/jpeg', 0.9);
    });
  }

  // Upload image to Cloudinary
  async uploadToCloudinary(file) {
    try {
      const formData = await this.createUploadFormData(file);
      
      const response = await fetch(`https://api.cloudinary.com/v1_1/${this.cloudinaryCloudName}/auto/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/137.0.0.0 Safari/537.36 Edg/137.0.0.0',
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'sec-ch-ua-platform': '"Windows"',
          'sec-ch-ua': '"Microsoft Edge";v="137", "Chromium";v="137", "Not/A)Brand";v="24"',
          'sec-ch-ua-mobile': '?0',
          'X-Unique-Upload-Id': Math.random().toString(36).substring(2) + '.' + Date.now(),
          'X-Requested-With': 'XMLHttpRequest',
          'Origin': 'https://upload-widget.cloudinary.com',
          'Sec-Fetch-Site': 'same-site',
          'Sec-Fetch-Mode': 'cors',
          'Sec-Fetch-Dest': 'empty',
          'Referer': 'https://upload-widget.cloudinary.com/',
          'Accept-Language': 'en-US,en;q=0.9'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload response:', errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.public_id) {
        throw new Error('Upload successful but no public_id returned');
      }


      return result;
      
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      throw new Error(`Failed to upload image: ${error.message}`);
    }
  }

  // Get processed image with background removed
  async getProcessedImage(publicId) {
    try {
      const imageUrl = `https://res.cloudinary.com/dtz0urit6/image/upload/e_background_removal/f_auto/${publicId}`;
      
      // Fetch the processed image
      const response = await fetch(imageUrl, {
        method: 'GET',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/137.0.0.0 Safari/537.36 Edg/137.0.0.0',
          'Accept': 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'sec-ch-ua-platform': '"Windows"',
          'sec-ch-ua': '"Microsoft Edge";v="137", "Chromium";v="137", "Not/A)Brand";v="24"',
          'sec-ch-ua-mobile': '?0',
          'sec-fetch-site': 'same-site',
          'sec-fetch-mode': 'no-cors',
          'sec-fetch-dest': 'image',
          'referer': 'https://cloudinary.com/',
          'accept-language': 'en-US,en;q=0.9'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get processed image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      return blob;
      
    } catch (error) {
      console.error('Error getting processed image:', error);
      throw new Error(`Failed to process image: ${error.message}`);
    }
  }

  // Main method to remove background from image
  async removeBackground(imageElement, options = {}) {
    try {
  
      
      // Convert image element to blob if needed
      let imageFile;
      if (imageElement instanceof File || imageElement instanceof Blob) {
        imageFile = imageElement;
      } else {
        imageFile = await this.imageElementToBlob(imageElement);
      }

      // Upload image to Cloudinary
      const uploadResult = await this.uploadToCloudinary(imageFile);
      
      // Get processed image with background removed
      const processedBlob = await this.getProcessedImage(uploadResult.public_id);
      

      return processedBlob;
      
    } catch (error) {
      console.error('Background removal failed:', error);
      throw error;
    }
  }

  // Alternative method for different image sources
  async removeBackgroundFromFile(file) {
    return this.removeBackground(file);
  }

  // Alternative method for data URLs
  async removeBackgroundFromDataURL(dataURL) {
    try {
      // Convert data URL to blob
      const response = await fetch(dataURL);
      const blob = await response.blob();
      return this.removeBackground(blob);
    } catch (error) {
      console.error('Error converting data URL:', error);
      throw error;
    }
  }

  // Check if service is ready
  isReady() {
    return this.isModelLoaded;
  }

  // Get service info
  getModelInfo() {
    return {
      type: 'Cloudinary AI',
      service: 'cloud-based',
      features: ['background_removal', 'high_quality', 'fast_processing'],
      status: 'ready'
    };
  }

  // Clean up method (for compatibility)
  dispose() {
    // No cleanup needed for API-based service
    console.log('Cloudinary AI service disposed');
  }
}

// Create singleton instance
const aiBackgroundRemover = new AIBackgroundRemover();

// Export functions for compatibility with existing code
export const removeBackground = (imageElement, options = {}) => {
  return aiBackgroundRemover.removeBackground(imageElement, options);
};

export const loadModel = () => {
  return aiBackgroundRemover.loadModel();
};

export const isModelLoaded = () => {
  return aiBackgroundRemover.isReady();
};

export const getModelInfo = () => {
  return aiBackgroundRemover.getModelInfo();
};

// Export the class instance as default
export default aiBackgroundRemover; 